import './MultiSelectDropdown.css';
import { useState, useRef, useEffect } from 'react';
import { HiOutlineChevronDown } from 'react-icons/hi';
import { FiCheck } from 'react-icons/fi';

interface MultiSelectDropdownProps {
  values: string[];
  options: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
  disabledOptions?: string[];
  hasSelectAll?: boolean;
}

function MultiSelectDropdown({
  values,
  options,
  onChange,
  placeholder = 'Select options',
  className = '',
  disabledOptions = [],
  hasSelectAll = true
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectableOptions = options.filter(opt => !disabledOptions.includes(opt));

  const handleToggle = (option: string) => {
    if (disabledOptions.includes(option)) return;

    if (option === 'All') {
      if (values.length === selectableOptions.length) {
        onChange([]);
      } else {
        onChange(selectableOptions);
      }
    } else {
      const newValues = values.includes(option)
        ? values.filter((v) => v !== option)
        : [...values, option];

      onChange(newValues);
    }
  };

  const displayText = (() => {
    if (values.length === 0) {
      return placeholder;
    }
    if (values.length === 1) {
      return values[0];
    }
    if (values.length === selectableOptions.length) {
      return 'All selected';
    }
    return `${values.length} selected`;
  })();

  const isAllChecked = selectableOptions.length > 0 && values.length === selectableOptions.length;

  const displayOptions = hasSelectAll ? ['All', ...options] : options;

  return (
    <div className={`multiselect-dropdown-wrapper ${className}`} ref={dropdownRef}>
      <button
        className="multiselect-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{displayText}</span>
        <HiOutlineChevronDown className={`dropdown-icon ${isOpen ? 'open' : ''}`} />
      </button>
      {isOpen && (
        <div className="multiselect-dropdown-menu">
          {displayOptions.map((option) => {
            let isChecked = false;

            if (option === 'All') {
              isChecked = isAllChecked;
            } else {
              isChecked = values.includes(option);
            }

            return (
              <button
                key={option}
                className={`multiselect-dropdown-item ${isChecked ? 'active' : ''} ${disabledOptions.includes(option) ? 'disabled' : ''}`}
                onClick={() => handleToggle(option)}
                disabled={disabledOptions.includes(option)}
              >
                <span className="checkbox-icon">
                  {isChecked && <FiCheck />}
                </span>
                <span className="multiselect-dropdown-item-label">{option}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MultiSelectDropdown;
